import * as Sentry from "@sentry/browser";

/**
 * Derive postcode, latitude, longitude from the project sites.
 * This is a workaround since this legacy comApp still assume there's only one address per project,
 * and does not support the concept of "a project can have many sites".
 *
 * @param {{ address: string, sites?: { address: string, postcode?: string | null, latitude?: number | null, longitude?: number | null }[] }} project
 * @returns {{ postcode: string | null, latitude: number | null, longitude: number | null }}
 */
export function inferProjectSiteFields(project) {
  const site = __resolveSite(project);
  if (!site) {
    return {
      postcode: null,
      latitude: null,
      longitude: null,
    };
  }

  return {
    postcode: site.postcode ?? null,
    latitude: toCoordinate(site.latitude),
    longitude: toCoordinate(site.longitude),
  };
}

/**
 * Select the site that matches the project address.
 */
function __resolveSite(project) {
  const sites = project?.sites ?? [];

  if (sites.length === 0) {
    Sentry.captureMessage(
      "inferProjectSiteFields: no sites found for project",
      {
        level: "error",
        tags: { reason: "no_sites" },
        extra: {
          projectAddress: project?.address ?? null,
          siteCount: sites.length,
        },
      }
    );
    return null;
  }

  if (sites.length === 1) {
    return sites[0];
  }

  const matchedSite = sites.find((site) => site.address === project.address);
  if (!matchedSite) {
    Sentry.captureMessage(
      "inferProjectSiteFields: no site address matches project address",
      {
        level: "error",
        tags: { reason: "no_matching_site" },
        extra: {
          projectAddress: project?.address ?? null,
          sites: project?.sites ?? [],
        },
      }
    );
    return null;
  }

  return matchedSite;
}

function toCoordinate(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const coordinate = typeof value === "number" ? value : Number(value);
  return Number.isFinite(coordinate) ? coordinate : null;
}
